<template>
  <div class="container-lg mt-[46px]">
    <div class="single">

      <!-- Product Preview Gallery Component -->
      <product-preview-gallery :in18="in18" :product="activeProduct"/>

      <div class="single__content">
        <!-- Product Breadcrumbs Component -->
        <product-breadcrumbs :in18="in18" :product="product"/>
        <!-- Product Title -->
        <h1 class="single__title mt-[24px]">{{ activeProduct.title }}</h1>
        <!-- Product Data -->
        <ul class="product__data">
          <li v-if="activeProduct.code" class="product_data-item">
            {{ in18['page_product_code'] }}
            <span class="fw-500">
              {{ activeProduct.code }}
            </span>
          </li>
          <li v-if="activeProduct.manufacture" class="product_data-item">
            {{ in18['page_manufacturer'] }}
            <a :title="activeProduct.manufacture.title" :href="activeProduct.manufacture.link" class=" text-primary fw-500">
              {{ activeProduct.manufacture.title }}
            </a>
          </li>

          <!--          <li class="product_data-item">-->
          <!--            {{ in18['page_availability'] }}-->
          <!--            <span :class="stockStatusClass">-->
          <!--              {{ stockStatusText }}-->
          <!--            </span>-->
          <!--          </li>-->
        </ul><!-- /.product__data -->

        <!-- Product Price Component -->
        <product-price
            :class-name="'single__price'"
            :prices="activeProduct.prices"
            :options="activeProduct"
            :in18="in18"
            :tax-explain-text="true"
        />

        <!-- Short Description -->
        <div v-if="product.short_description" class="mb-4 pt-0 text-[16px] leading-[24px] font-light text-medium-gray">
          <div :class="{'expanded': showReadMoreLink }">{{ displayedDescription }}</div>
          <a v-if="showReadMoreLinkButton" :title="in18['read_more_label']" class="link link--primary fw-500" href="#"
             @click.prevent="toggleReadMoreLink">
            {{ in18['read_more_label'] }}
          </a>
        </div><!-- /.single__text -->

        <!-- Variations -->
        <div v-if="product.variations.length > 0" class="variations">
          <h3 class="variations__title">
            {{ in18['page_variations'] }}:
          </h3>

          <ul v-if="activeProduct" class="product__data product__data mb-4">
            <li v-for="property in activeProduct.properties" class="product_data-item">
              {{ property.property_title }}: <span class="text-primary fw-500">{{ property.option_value }}</span>
            </li>
          </ul>

          <div class="variations__list">
            <div v-for="variation in product.variations" class="variations__item" >
              <!-- Variation Input -->
              <input :id="'variations_' + variation.id"
                     v-model="selectedVariation"
                     :value="variation"
                     class="variations__input"
                     name="variations"
                     type="radio"
              >
              <!-- Variation Label -->
              <label :for="'variations_' + variation.id" :title="variation.title" class="variations__label">
                <img :alt="variation.alt"
                     :src="variation.image.source"
                     class="variations__img"
                     height="52"
                     width="52"
                >
              </label>
            </div>
          </div>
        </div>

        <!-- Product Footer -->
        <div v-if="showActionForms" class="single__footer">
          <div class="single__footer-add">
            <!-- Product Counter Component -->
            <product-counter
                @update-select-quantity="updateSelectQuantity"
            />
            <!-- Add to Cart Button Component -->
            <add-to-cart-button
                :className="'btn btn--primary m-0'"
                :in18="in18"
                :product-id="activeProduct.id"
                :select-quantity="selectQuantity"
                :title="in18['page_add_to_cart']"
            />
          </div>
          <div class="single__footer-btns">
            <!-- Add to Wishlist Button Component -->
            <add-to-wishlist-button :class-name="'btn  btn--square'" :in18="in18" :product-id="activeProduct.id"/>
            <!-- Add to Compare List Button Component -->
            <add-to-compare-list-button :class-name="'btn  btn--square'" :in18="in18" :product-id="activeProduct.id"/>
          </div>

          <template v-if="activeProduct.quantity === 0 && activeProduct.virtual_quantity === 0">
            <!-- Action buttons -->
            <h3 class="single__footer-title">
              {{ in18['page_you_can_leave_your_email_address'] }}
            </h3>

            <!-- Subscribe Form -->
            <div class="subscribe subscribe__form my-0 p-0">
              <!-- Subscriber Email Input -->
              <input v-model="subscriberEmail"
                     :placeholder="in18['attribute_enter_email']"
                     class="subscribe__input"
                     type="email"
                     @click="initRecaptcha"
              >
              <!-- Subscribe Button -->
              <button :disabled="isDisableSubscribeBtn"
                      class="btn btn--green mb-0"
                      type="submit"
                      @click="subscribeToProduct">
                {{ in18['button_subscribe'] }}
              </button>
              <!-- Google reCAPTCHA -->
              <google-recaptcha-v3
                  id="register_us_id"
                  ref="captcha"
                  v-model="gRecaptchaResponse"
                  :site-key="captcha_site_key"
                  action="product_subscribe_action"
                  corner
              />
            </div>
          </template>
        </div><!-- /.single__footer -->

        <!-- Product Credit Prices Component -->
        <!-- <product-credit-prices :in18="in18" :product="activeProduct"/> -->

        <ul class="delivery-options">
          <li v-for="shipping_delivery_day in activeProduct.shipping_delivery_days" class="delivery-options__item">
            <img :alt="shipping_delivery_day.title" class="img" :src="shipping_delivery_day.icon">
            <div class="delivery-options__title">
              {{ shipping_delivery_day.title }}
            </div>
            <div class="delivery-options__text">
              <div v-html="shipping_delivery_day.days"></div>
              <div v-html="shipping_delivery_day.price"></div>
            </div>
          </li>
        </ul><!-- /.delivery-options -->

        <div class="text-sm text-gray">{{ in18['page_estimated_delivery_dates_message'] }}</div>

      </div>
    </div><!-- /.single -->
  </div><!-- /.container -->
</template>

<script>

import ProductBreadcrumbs from "./ProductBreadcrumbs.vue";
import ProductPreviewGallery from "./ProductPreviewGallery.vue";
import ProductCreditPrices from "./ProductCreditPrices.vue";
import ProductCounter from "./ProductCounter";
import AddToWishlistButton from "./AddToWishlistButton.vue";
import AddToCompareListButton from "./AddToCompareListButton.vue";
import AddToCartButton from "./AddToCartButton.vue";
import {isValidEmail} from "../../helpers/is-valid-email";

export default {
  name:       'ProductHeader',
  components: {
    'product-preview-gallery':    ProductPreviewGallery,
    'product-breadcrumbs':        ProductBreadcrumbs,
    'product-credit-prices':      ProductCreditPrices,
    'product-counter':            ProductCounter,
    'add-to-wishlist-button':     AddToWishlistButton,
    'add-to-compare-list-button': AddToCompareListButton,
    'add-to-cart-button':         AddToCartButton,
  },
  props:      {
    product:          Object,
    in18:             {
      type: Object,
    },
    captcha_site_key: String,
    activeProduct:    Object,
  },
  data() {
    return {
      showReadMoreLink:          false,
      subscriberEmail:           '',
      gRecaptchaResponse:        null,
      selectQuantity:            1,
      selectedVariation:         this.activeProduct,
      maxShortDescriptionLength: 300,
      recaptchaLoaded:           false,
    }
  },

  mounted() {
    ga_view_item_event(this.product, 0, this.product.prices.current_price);
  },

  computed: {

    showActionForms() {
      return !(this.activeProduct.hasOwnProperty('variations') && this.activeProduct.variations.length > 0);
    },

    /**
     * Determines the text to display for the stock status based on the quantity of the active product.
     * @returns {string} Stock status text.
     */
    stockStatusText() {
      if (this.activeProduct.quantity > 0) {
        return this.in18['page_product_in_shop'];
      } else if (this.activeProduct.virtual_quantity > 0) {
        return this.in18['page_product_in_stock'];
      } else {
        return this.in18['page_product_to_order'];
      }
    },

    /**
     * Determines the CSS class for styling the stock status based on the quantity of the active product.
     * @returns {string} CSS class for the stock status text.
     */
    stockStatusClass() {
      return (this.activeProduct.quantity > 0 || this.activeProduct.virtual_quantity > 0) ? 'text-green fw-500' : 'text-red fw-500';
    },

    /**
     * Checks whether the subscribe button should be disabled based on the validity of the subscriber's email.
     * @returns {boolean} True if the subscribe button should be disabled, otherwise false.
     */
    isDisableSubscribeBtn() {
      return !isValidEmail(this.subscriberEmail);
    },

    /**
     * Checks whether the "Read More" link button should be displayed.
     * @returns {boolean} True if the "Read More" link button should be displayed, otherwise false.
     */
    showReadMoreLinkButton() {
      // Show the "Read More" link button if the short description exceeds the maximum length and is not expanded.
      return !this.showReadMoreLink && this.product.short_description.length > this.maxShortDescriptionLength;
    },

    /**
     * Determines the displayed description, either full or truncated, based on the "Read More" link state.
     * @returns {string} The displayed short description.
     */
    displayedDescription() {
      if (this.product.short_description.length > this.maxShortDescriptionLength) {
        return this.showReadMoreLink
            ? this.product.short_description
            : this.product.short_description.slice(0, this.maxShortDescriptionLength) + '...';
      } else {
        return this.product.short_description;
      }
    },
  },

  watch: {
    activeProduct(newActiveProduct) {
      this.selectedVariation = newActiveProduct;
    },

    selectedVariation(val) {
      this.$emit('change-active-product', val);
      this.selectQuantity = 1;
      // Committing the quantity change to the Vuex store
      this.$store.commit('SET_QUANTITY', 1);
      this.subscriberEmail = ''; // Clearing subscriber email when the product details are updated.
    },
  },

  emits: [
    'change-active-product'
  ],

  methods: {

    initRecaptcha() {
      if (!this.recaptchaLoaded) {
        this.$refs.captcha.init();
        this.recaptchaLoaded = true;
      }
    },

    /**
     * Toggles the visibility of the "Read More" link for the product description.
     */
    toggleReadMoreLink() {
      this.showReadMoreLink = !this.showReadMoreLink;
    },

    /**
     * Updates the selected quantity based on the user's input.
     * @param {number} newSelectQuantity - The new selected quantity.
     */
    updateSelectQuantity(newSelectQuantity) {
      this.selectQuantity = newSelectQuantity;
    },

    /**
     * Subscribes the user to receive updates about the product.
     * Validates the subscriber's email and sends a subscription request to the server.
     * Displays success or error messages using Toastr.
     * @returns {boolean} - True if the subscription is successful, otherwise false.
     */
    async subscribeToProduct() {
      // Validate the subscriber's email before proceeding
      if (!isValidEmail(this.subscriberEmail)) {
        return false;
      }

      try {
        // Send a subscription request to the server
        const response = await axios.post('/api/product-subscribe', {
          product_id:         this.activeProduct.id,
          email:              this.subscriberEmail,
          gRecaptchaResponse: this.gRecaptchaResponse,
        });

        // Clear the subscriber's email and show success message
        this.subscriberEmail = '';
        window.toastr.success(response.data.message);
      } catch (error) {
        // Show error message in case of subscription failure
        window.toastr.error(error.response.data.message);
      }
    }
  }
};
</script>
